import React, { useState, useRef, useEffect, useMemo } from "react";
import apiClient from "../../../../../../Config/apiClient";
import moment from "moment";
import { ClipLoader } from "react-spinners";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ConfirmationModal from "../../../../../../Components/ConfirmationBox/ConfirmationModal";
import { showSuccessToast, showErrorToast } from "../../../../../../Context/Toaster/Toaster";

import AddEditNoteModal from "../AddNotesModal/AddNotesModal";
import deleteIcon from "../../../../../../Assets/images/delete-icon.svg";
import addNotesIcon from "../../../../../../Assets/images/add-notes.png";
import filterIcon from "../../../../../../Assets/images/filter-icon.svg";
import closeRedIcon from "../../../../../../Assets/images/close-red.svg";
import calendarIcon from "../../../../../../Assets/images/calender.svg";
import clearIcon from "../../../../../../Assets/images/clear-icon.svg";
import editIcon from "../../../../../../Assets/images/edit-icon.svg";
import { DATE_FORMAT, DATE_MONTH_FORMAT } from "../../../Utils/Constants";

const notesBaseUrl = process.env.REACT_APP_CC_NOTES_BASE_PATH;

const NotesList = ({ canEditNotes }) => {
  const [notesData, setNotesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);

  const [filters, setFilters] = useState({
    jobName: "",
    dateFrom: null,
    dateTo: null,
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const [filterErrors, setFilterErrors] = useState({
    dateFrom: "",
    dateTo: "",
  });

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [checkedNotes, setCheckedNotes] = useState(new Set());
  const [parentChecked, setParentChecked] = useState(false);

  const fetchNotes = async (page = 1, filtersToApply = appliedFilters) => {
    try {
      setLoading(true);
      const params = {};
      if (filtersToApply.jobName) params.job_name = filtersToApply.jobName;
      if (filtersToApply.dateFrom)
        params.start_date = moment(filtersToApply.dateFrom).format(DATE_FORMAT);
      if (filtersToApply.dateTo)
        params.end_date = moment(filtersToApply.dateTo).format(DATE_FORMAT);

      const res = await apiClient.get(`${notesBaseUrl}/`, { params });
      if (res.data?.status === "SUCCESS") {
        setNotesData(res.data.data || []);
        setCurrentPage(page);
      } else {
        setNotesData([]);
      }
    } catch {
      setNotesData([]);
    } finally {
      setLoading(false);
      setCheckedNotes(new Set());
      setParentChecked(false);
    }
  };

  const handleDeleteClick = (note) => {
    setSelectedNote(note);
    setShowConfirmModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (selectedNote?.multiple) {
        const response = await apiClient.delete(`${notesBaseUrl}/delete-multiple`, {
          data: { ids: selectedNote.ids },
        });

        if (response.data.status === "SUCCESS") {
          showSuccessToast(response.data.message || "Notes deleted successfully.");
          fetchNotes(currentPage);
        } else {
          showErrorToast(response.data.message || "Failed to delete notes.");
        }
      } else {
        const response = await apiClient.delete(`${notesBaseUrl}/delete/${selectedNote.id}`);

        if (response.data.status === "SUCCESS") {
          showSuccessToast(response.data.message || "Note deleted successfully.");
          fetchNotes(currentPage);
        } else {
          showErrorToast(response.data.message || "Failed to delete note.");
        }
      }
    } catch (err) {
      showErrorToast("An error occurred while deleting.");
    } finally {
      setShowConfirmModal(false);
      setSelectedNote(null);
    }
  };

  const handleSaveNote = async (noteData) => {
    try {
      if (editingNote) {
        const response = await apiClient.put(
          `${notesBaseUrl}/edit-notes/${editingNote.id}`,
          { description: noteData.description }
        );

        if (response.data.status === "SUCCESS") {
          showSuccessToast(`Note updated Succesfully`);
          fetchNotes(currentPage);
        } else {
          showErrorToast(
            `Failed to update note`
          );
        }

      } else {
        const response = await apiClient.post(`${notesBaseUrl}/add-notes`, {
          job_id: noteData.jobId,
          description: noteData.description,
        });

        if (response.data.status === "SUCCESS") {
          showSuccessToast(`Note added Succesfully`);
          fetchNotes(1);
        } else {
          showErrorToast(
            `Failed to add note`
          );
        }
      }
    } catch (err) {
      if (editingNote) {
        showErrorToast(`Error updating note`);
      } else {
        showErrorToast(`Error adding note`);
      }
    } finally {
      setShowNoteModal(false);
      setEditingNote(null);
    }
  };

  useEffect(() => {
    fetchNotes(1, appliedFilters);
  }, [itemsPerPage]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleFilter = () => setIsFilterOpen((prev) => !prev);

  const clearAllFilters = () => {
    const cleared = { jobName: "", dateFrom: null, dateTo: null };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setFilterErrors({ dateFrom: "", dateTo: "" });
    fetchNotes(1, cleared);
  };

  const validateFilters = () => {
    let errors = { dateFrom: "", dateTo: "" };
    let isValid = true;

    if (filters.dateFrom && !filters.dateTo) {
      errors.dateTo = "End Date is required.";
      isValid = false;
    } else if (!filters.dateFrom && filters.dateTo) {
      errors.dateFrom = "Start Date is required.";
      isValid = false;
    } else if (filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo) {
      errors.dateTo = "End Date must be after Start Date.";
      isValid = false;
    }

    setFilterErrors(errors);
    return isValid;
  };

  const applyFilters = () => {
    if (!validateFilters()) {
      return;
    }
    setAppliedFilters(filters);
    fetchNotes(1, filters);
    setIsFilterOpen(false);
  };

  const totalNotes = notesData.length;
  const totalPages = Math.ceil(totalNotes / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  const currentNotes = useMemo(() => {
    return notesData.slice(startIndex, startIndex + itemsPerPage);
  }, [notesData, startIndex, itemsPerPage]);

  useEffect(() => {
    setParentChecked(
      currentNotes.length > 0 &&
      currentNotes.every((note) => checkedNotes.has(note.id))
    );
  }, [currentNotes, checkedNotes]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  useEffect(() => {
    setCheckedNotes(new Set());
    setParentChecked(false);
  }, [currentPage, itemsPerPage, notesData]);

  const isAnyFilterApplied =
    appliedFilters.jobName ||
    appliedFilters.dateFrom !== null ||
    appliedFilters.dateTo !== null;

  return (
    <main className="full-wraper">
      <div className="main-wraper">
        <div className="container-fluid">
          <div className="main-inner">
            <div className="common-top">
              {canEditNotes && (
                <a
                  className={`topdelete-button ${checkedNotes.size === 0 ? "disabled" : ""}`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (checkedNotes.size === 0) return;
                    setSelectedNote({ multiple: true, ids: Array.from(checkedNotes) });
                    setShowConfirmModal(true);
                  }}
                  style={{
                    pointerEvents: checkedNotes.size === 0 ? "none" : "auto",
                    opacity: checkedNotes.size === 0 ? 0.5 : 1,
                  }}
                >
                  <img className="ct-icon" src={deleteIcon} alt="Delete" /> Delete
                </a>
              )}

              {canEditNotes && (
                <a
                  className="topadd-button"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setEditingNote(null);
                    setShowNoteModal(true);
                  }}
                >
                  <img className="ct-icon" src={addNotesIcon} alt="Add" /> Add
                </a>
              )}

              <div className="filter-cvr dropdown" ref={dropdownRef}>
                <a
                  className={`filter-btn ${isAnyFilterApplied ? "active-filter" : ""}`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    toggleFilter();
                  }}
                >
                  <span className="filter-icon-wrapper">
                    <img className="ct-icon" src={filterIcon} alt="Filter" />
                    {isAnyFilterApplied && <span className="filter-indicator-dot"></span>}
                  </span>
                  Filter
                </a>
                <a
                  className="clear-btn"
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    clearAllFilters();
                  }}
                >
                  <img className="ct-icon" src={clearIcon} alt="Clear" /> Clear Filter
                </a>
                {isFilterOpen && (
                  <ul className="dropdown-menu filterdrop-body show">
                    <div className="filterdrop-split">
                      <label className="filter-label">
                        Job Id
                        {filters.jobName && (
                          <img
                            className="red-close"
                            src={closeRedIcon}
                            alt="remove"
                            style={{ cursor: "pointer" }}
                            onClick={() => setFilters((prev) => ({ ...prev, jobName: "" }))}
                          />
                        )}
                      </label>
                      <input
                        type="text"
                        className="filter-select"
                        value={filters.jobName}
                        onChange={(e) =>
                          setFilters((prev) => ({ ...prev, jobName: e.target.value }))
                        }
                      />
                    </div>

                    <div className="filterdrop-split">
                      <label className="filter-label">
                        Created Date
                        {(filters.dateFrom || filters.dateTo) && (
                          <img
                            className="red-close"
                            src={closeRedIcon}
                            alt="remove"
                            style={{ cursor: "pointer" }}
                            onClick={() => setFilters((prev) => ({ ...prev, dateFrom: null, dateTo: null }))}
                          />
                        )}
                      </label>
                      <div className="filter-datepicker from-date">
                        <DatePicker
                          selected={filters.dateFrom}
                          onChange={(date) => setFilters((prev) => ({ ...prev, dateFrom: date }))}
                          placeholderText="Start Date"
                          className="filter-date"
                          dateFormat={DATE_MONTH_FORMAT}
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          popperProps={{ strategy: 'fixed' }}
                        />
                        <span className="filter-calendar-icon">
                          <img src={calendarIcon} alt="Calendar" />
                        </span>
                        {filterErrors.dateFrom && (
                          <div className="error-text active">{filterErrors.dateFrom}</div>
                        )}
                      </div>
                      <div className="filter-datepicker to-date">
                        <DatePicker
                          selected={filters.dateTo}
                          onChange={(date) => setFilters((prev) => ({ ...prev, dateTo: date }))}
                          placeholderText="End Date"
                          className="filter-date"
                          dateFormat={DATE_MONTH_FORMAT}
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          popperProps={{ strategy: 'fixed' }}
                        />
                        <span className="filter-calendar-icon">
                          <img src={calendarIcon} alt="Calendar" />
                        </span>
                      </div>
                      {filterErrors.dateTo && (
                        <div className="error-text active">{filterErrors.dateTo}</div>
                      )}
                    </div>

                    <div className="filterdrop-split mb-0">
                      <div className="filter-buttons">
                        <a
                          href="#"
                          className="filter-clear"
                          onClick={(e) => {
                            e.preventDefault();
                            clearAllFilters();
                          }}
                        >
                          Clear
                        </a>
                        <a
                          href="#"
                          className="filter-apply"
                          onClick={(e) => {
                            e.preventDefault();
                            applyFilters();
                          }}
                        >
                          Apply
                        </a>
                      </div>
                    </div>
                  </ul>
                )}
              </div>
            </div>
            <div className="table-cvr">
              <div className="table-inner">
                <table className="table">
                  <colgroup>
                    <col style={{width: '2%'}}/>
                    <col style={{width: '5%'}}/>
                    <col style={{width: '10%'}}/>
                    <col style={{width: '25%'}}/>
                    <col style={{width: '19%'}}/>
                    <col style={{width: '11%'}}/>
                    <col style={{width: '13%'}}/>
                    <col style={{width: '12%'}}/>
                    <col style={{width: '3%'}}/>
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="col-checkbox">
                        <input
                          type="checkbox"
                          className="common-check"
                          checked={parentChecked}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setParentChecked(checked);
                            if (checked) {
                              setCheckedNotes(new Set(currentNotes.map((note) => note.id)));
                            } else {
                              setCheckedNotes(new Set());
                            }
                          }}
                        />
                      </th>
                      <th className="">#</th>
                      <th className="note-postedcol">Posted On</th>
                      <th>Note</th>
                      <th>Job</th>
                      <th>Job Id</th>
                      <th>Supervisor</th>
                      <th>Contact No.</th>
                      {canEditNotes && (
                        <th></th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          <ClipLoader size={30} color="gray" />
                        </td>
                      </tr>
                    ) : currentNotes.length > 0 ? (
                      currentNotes.map((note, idx) => (
                        <tr key={note.id}>
                          <td>
                            <input
                              type="checkbox"
                              className="parent-checkbox common-check"
                              checked={checkedNotes.has(note.id)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setCheckedNotes((prev) => {
                                  const updated = new Set(prev);
                                  if (checked) {
                                    updated.add(note.id);
                                  } else {
                                    updated.delete(note.id);
                                  }
                                  setParentChecked(updated.size === currentNotes.length);
                                  return updated;
                                });
                              }}
                            />
                          </td>
                          <td className="">{startIndex + idx + 1}</td>
                          <td className="note-postedcol">
                            {note.created_at
                              ? moment.utc(note.created_at).local().format(DATE_FORMAT)
                              : ""}
                          </td>
                          <td>{note.description}</td>
                          <td>{note.job_description}</td>
                          <td>{note.job_name}</td>
                          <td>{note.supervisor_name}</td>
                          <td>{note.supervisor_contact}</td>
                          {canEditNotes && (
                            <td className="edit-col">
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setEditingNote(note);
                                  setShowNoteModal(true);
                                }}
                              >
                                <img className="tb-icon" src={editIcon} alt="Edit" />
                              </a>
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center py-4 text-muted">
                          No notes found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="dt-layout-row d-flex align-items-center justify-content-between mt-3">
                <div className="dt-length">
                  Showing{" "}
                  <select
                    className="dt-input"
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(+e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    {[5, 10, 50, 100].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>{" "}
                  of {totalNotes} entries
                </div>
                <div className="dt-paging d-flex align-items-center" style={{ gap: "5px" }}>
                  <button
                    className="dt-paging-button previous"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i + 1}
                      className={`dt-paging-button ${currentPage === i + 1 ? "current" : ""}`}
                      onClick={() => handlePageChange(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className="dt-paging-button next"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AddEditNoteModal
        show={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        onSave={handleSaveNote}
        initialData={editingNote}
        jobId={editingNote ? editingNote.job_id : null}
        allowJobSelection={!editingNote}
      />

      {showConfirmModal && (
        <ConfirmationModal
          id="deleteNoteModal"
          show={showConfirmModal}
          title="Confirm Deletion"
          message={
            selectedNote?.multiple
              ? `Are you sure you want to delete ${selectedNote.ids.length} selected notes?`
              : `Are you sure you want to delete the note "${selectedNote?.description || ""}"?`
          }
          confirmText="Yes"
          cancelText="Cancel"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setShowConfirmModal(false)}
        />

      )}

    </main>
  );
};

export default NotesList;
